import React, { useState } from 'react'
import { useStore } from '@nanostores/react'
import { isCartOpen, cartData, hasCartQuantityError } from '@stores/cartStore.js'
import Modal from 'react-modal'
import { userData } from '@stores/userStore'
import { createCart, addToCart } from '@api/shopify.js'
import { chord } from '@api/chord.js'
import va from '@vercel/analytics'
import PriceNosto from '@components/Price/PriceNosto.jsx'
import SizeGuide from '@components/SizeGuide/SizeGuide.jsx'
import parse from 'html-react-parser'
import Heart from '@components/Icons/Heart.jsx'

import {
    slugify,
    SIZES,
    convertToCurrency,
} from '@utils/utils.js'

Modal.setAppElement('*') // suppresses modal-related test warnings.

const FILES_URL = 'https://cdn.shopify.com/s/files/1/2338/2917/files/'

const DEFAULT_IMAGE_SRC =
    'https://cdn.shopify.com/s/files/1/2338/2917/files/default-image.png?v=1628278950'

const registryHtml = `<span class="flex items-center justify-center gap-2"><div class='heart-icon w-[24px] max-w-[24px]'><svg xmlns="http://www.w3.org/2000/svg" x="0" y="0" enableBackground="new 0 0 100 100" version="1.1" viewBox="0 0 100 100" xmlSpace="preserve"><path d="M49.9 91.9L8.1 50.1c-9-9-9-23.6 0-32.6s23.6-9 32.6 0l9.2 9.2 9.2-9.2c9-9 23.6-9 32.6 0s9 23.6 0 32.6L49.9 91.9zM12.6 22c-6.5 6.5-6.5 17.1 0 23.6l37.3 37.3 37.3-37.3c6.5-6.5 6.5-17.1 0-23.6s-17.1-6.5-23.6 0L49.9 35.7 36.2 22c-6.5-6.5-17.1-6.5-23.6 0z" className="st0" fill="#414042"></path></svg></div>Add To Babylist Registry</span>`

const QuickBuyNosto = ({
    product,
    quickBuyActive,
    setQuickBuyActive,
    variant,
    variants,
    variantId,
    image,
}) => {
    const [cart, setCart] = useState()
    const $cartData = useStore(cartData)
    const $user = useStore(userData)
    const [value, setValue] = useState('Size')
    const [currentVariantId, setCurrentVariantId] = useState(variantId || '')
    const [currentVariantPrice, setCurrentVariantPrice] = useState({
        price: variant?.price || '',
        compareAtPrice: variant?.listPrice || '',
    })

    let productHandle
    if (product?.productHandle) {
        productHandle = product.productHandle.split('_')[0]
    }

    // Get available sizes from SKUs' variantCustomFields
    const availableSizes = product?.skus?.reduce((acc, sku) => {
        // Check if we have valid inventory
        const inventoryLevel = parseInt(sku.inventoryLevel)
        if (inventoryLevel <= 0) return acc

        // Parse variantCustomFields
        if (sku.variantCustomFields && typeof sku.variantCustomFields === 'string') {
            try {
                // Remove the outer brackets and split by comma
                const fields = sku.variantCustomFields
                    .replace(/^\[|\]$/g, '') // Remove outer brackets
                    .split(',')
                    .map(field => field.trim())

                // Find the size field
                const sizeField = fields.find(field => field.startsWith('size='))
                if (sizeField) {
                    // Extract the size value
                    const size = sizeField.split('=')[1]
                        .trim()
                        .replace(/['"]/g, '') // Remove any quotes
                        .trim()

                    // Only add if we have a valid size and it's not already in the array
                    if (size && !acc.find(item => item.size === size)) {
                        acc.push({
                            size,
                            variantId: sku.id
                        })
                    }
                }
            } catch (error) {
                console.error('Error parsing variantCustomFields:', error, sku.variantCustomFields)
            }
        }
        return acc
    }, []).sort((a, b) => SIZES.indexOf(a.size) - SIZES.indexOf(b.size)) || []

    // Debug log for specific product
    if (product?.productName?.includes('Classic Leggings_Fresh Florals')) {
        console.log('Product SKUs:', product.skus)
        console.log('Available sizes:', availableSizes)
    }

    const handleSizeClick = (size, variantId) => {
        if (!variantId) return
        setValue(size)
        setCurrentVariantId(variantId)
        const selectedVariant = variants.find(v => v.id === variantId)
        if (selectedVariant) {
            setCurrentVariantPrice({
                price: selectedVariant.price,
                compareAtPrice: selectedVariant.listPrice,
            })
        }
    }

    // Find first variant with inventory
    const firstVariantWithInventory = product?.skus?.find(
        (sku) => parseInt(sku.inventoryLevel) > 0
    )

    console.log('QuickBuyNosto product data:', {
        productPrice: product?.productPrice,
        productListPrice: product?.productListPrice,
        productDiscountedPrice: product?.productDiscountedPrice,
        productDiscounted: product?.productDiscounted,
        productDiscountAmount: product?.productDiscountAmount,
        productDiscountPercentage: product?.productDiscountPercentage,
        skus: product?.skus,
        currentVariant: firstVariantWithInventory
    })

    const handleAddToCart = async (e) => {
        e.preventDefault()
        if (!currentVariantId) return

        try {
            let cartId = $cartData.cartId || ''

            if (!cartId || cartId === '') {
                let cartCreateData = await createCart()
                cartId = cartCreateData.data.cartId

                let cartObj = {
                    ...cartData.get(),
                    cartId: cartId,
                    checkoutUrl: cartCreateData.data.checkoutUrl,
                }

                cartData.set(cartObj)
            }

            const lineItems = [
                {
                    quantity: 1,
                    merchandiseId: `gid://shopify/ProductVariant/${currentVariantId}`,
                    attributes: [
                        {
                            key: '_pid',
                            value: `${product.id}`,
                        },
                    ],
                },
            ]

            const addToCartData = await addToCart(cartId, lineItems)

            if (!addToCartData?.body?.cartLinesAdd?.cart) {
                hasCartQuantityError.set(true)
                isCartOpen.set('is_cart_open', true)
                return
            }

            let cartLines = addToCartData.body.cartLinesAdd.cart.lines.edges
            let cartCost = addToCartData.body.cartLinesAdd.cart.cost

            let cartObj = {
                ...cartData.get(),
                lines: cartLines,
                cost: cartCost,
            }

            cartData.set(cartObj)
            isCartOpen.set('is_cart_open', true)

            va.track('Add to Cart', {
                product: product?.productName,
                variant: currentVariantId,
                quantity: 1,
            })

            chord.trackProductAdded({
                cart: $cartData,
                product: {
                    product,
                    variantId: currentVariantId,
                },
            })

            setQuickBuyActive(false)
        } catch (error) {
            console.error('Error adding to cart:', error)
        }
    }

    /* Registry */
    const handleAddToBabyList = (url, title, image) => {
        bl.addToRegistry({
            images: image,
            price: currentVariantPrice.price,
            title: title,
            url: url,
        })
    }

    return (
        <Modal
            isOpen={quickBuyActive}
            onRequestClose={() => setQuickBuyActive(false)}
            className="quick-buy-modal"
            overlayClassName="quick-buy-overlay w-full z-[99999]"
            contentLabel="Quick Buy Modal"
        >
            <div
                id="quick-buy"
                className="quick-buy-outer px-6 pt-6 pb-6 lg:p-6 relative bg-white flex mx-auto gap-4 max-h-[80vh] overflow-y-auto"
            >
                <div className="modal-close flex items-center absolute -top-1 lg:top-2 right-1 lg:right-3">
                    <span
                        className="close-modal cursor-pointer text-2xl lg:text-[36px] font-normal text-[#B8B9BA] hover:text-ash"
                        onClick={() => setQuickBuyActive(false)}
                    >
                        &times;
                    </span>
                </div>
                <div className="product-modal-outer w-full">
                    <div className="product-modal-inner relative flex flex-wrap sm:flex-nowrap gap-x-6 lg:gap-6">
                        <div className="product-image relative flex flex-col items-center w-full sm:max-w-[380px]">
                            <div className="image-container flex items-center justify-center w-full aspect-[2/3] overflow-hidden relative bg-[#f8f8f8]">
                                <a
                                    className="image-click w-full h-full flex items-center justify-center no-underline"
                                    href={`/products/${productHandle}${currentVariantId ? `?variant=${currentVariantId}` : ''}`}
                                >
                                    <img
                                        className="w-full h-full object-cover"
                                        src={image || product?.productImageUrl || product?.primaryImageUrl}
                                        alt={parse(product?.productName || '')?.replace('_', ' - ')}
                                        onError={(e) => {
                                            if (e.target.src !== DEFAULT_IMAGE_SRC) {
                                                e.target.onerror = null
                                                e.target.src = DEFAULT_IMAGE_SRC
                                            }
                                        }}
                                    />
                                </a>
                            </div>
                            <p className="text-xs my-2 text-flint hover:text-ash">
                                <a
                                    className="underline"
                                    href={`/products/${productHandle}${currentVariantId ? `?variant=${currentVariantId}` : ''}`}
                                >
                                    View full product details
                                </a>
                            </p>
                        </div>

                        <div className="product-inner relative w-full">
                            <div className="product-top">
                                <h3 className="product-title text-lg lg:text-2xl mb-1 lg:mb-2">
                                    <a
                                        className="no-underline hover:underline"
                                        href={`/products/${productHandle}${currentVariantId ? `?variant=${currentVariantId}` : ''}`}
                                    >
                                        {parse(product?.productName || '')?.replace('_', ' - ')}
                                    </a>
                                </h3>

                                <PriceNosto
                                    product={product}
                                    priceType="quickBuy"
                                    matchingVariants={variants}
                                    variantIdOnLoad={variantId}
                                />
                            </div>

                            <div className="product-bottom pt-2 lg:pt-4">
                                <div className="product-actions">
                                    {availableSizes.length > 0 && (
                                        <div className="product-sizes">
                                            <div className="product-sizes py-2 lg:py-6 border-t border-b border-y-slate">
                                                <div className="size-text text-xs flex items-center mb-2">
                                                    <h2 className="text-sm">Choose A Size</h2>
                                                    <span className="current-size-text block ml-auto">
                                                        <span className="font-semibold">Selected:</span>{' '}
                                                        {value !== 'Size' ? value : '(none)'}
                                                    </span>
                                                </div>
                                                <ul className="size-list nostyle grid grid-cols-4 lg:grid-cols-6 gap-1 lg:gap-2">
                                                    {availableSizes.map(({ size, variantId }) => (
                                                        <li
                                                            key={size}
                                                            className={`size h-[24px] lg:h-[36px] border border-oat rounded-sm flex items-center justify-center px-2 text-sm hover:cursor-pointer hover:bg-flint hover:text-white ${
                                                                value === size
                                                                    ? 'size-selected bg-ash text-white'
                                                                    : 'bg-white'
                                                            }`}
                                                            onClick={() => handleSizeClick(size, variantId)}
                                                        >
                                                            <span className="size-label text-xs">{size}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                                <div className="size-guide-wrap ml-auto mt-2">
                                                    <SizeGuide />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="add-to-wrap flex items-center flex-wrap w-full">
                                        <div className="product-add-to-cart w-full">
                                            <button
                                                type="button"
                                                className={`large-button button-atc w-full h-42px lg:!h-[60px] ${
                                                    !currentVariantId ? 'disabled noclick' : ''
                                                }`}
                                                onClick={handleAddToCart}
                                                disabled={!currentVariantId}
                                            >
                                                Add To Cart
                                            </button>
                                        </div>

                                        <div className="registry-actions flex items-center justify-center mt-2 w-full">
                                            <button
                                                className={`registry-trigger large-button button-registry text-sm !h-[42px] gap-2 w-full ${
                                                    !currentVariantId ? 'disabled noclick opacity-80' : 'hover:bg-oat'
                                                }`}
                                                data-variant-id={currentVariantId}
                                                data-balloon-blunt
                                                data-balloon-pos="up"
                                                aria-label={!currentVariantId ? 'Please select a size' : null}
                                                onClick={() => {
                                                    if (!currentVariantId) return
                                                    handleAddToBabyList(
                                                        `${location.origin}/products/${productHandle}${
                                                            currentVariantId ? `?variant=${currentVariantId}` : ''
                                                        }`,
                                                        product.productName,
                                                        image || product?.productImageUrl || product?.primaryImageUrl
                                                    )
                                                }}
                                            >
                                                <span className="flex items-center justify-center gap-2 w-full">
                                                    <Heart width="w-[24px]" />
                                                    Add To Babylist Registry
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="close-text flex items-center justify-center text-center mx-auto mt-2">
                <p
                    className="font-normal text-sm underline text-center mt-2"
                    onClick={() => setQuickBuyActive(false)}
                >
                    Close
                </p>
            </div>
        </Modal>
    )
}

export default QuickBuyNosto
